import { useResendCooldown } from '@lib/hooks/auth/useResendCooldown';
import { useTurnstile } from '@lib/hooks/auth/useTurnstile';
import { ApiError } from '@lib/network/api';
import { isEmailValid } from '@lib/utils/email';
import { Box } from '@mui/material';
import { loginPreferences } from '@stores/LoginPreferences';
import { trackingStore } from '@stores/Tracking';
import { userStore } from '@stores/User';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AuthButton } from '../AuthButton';
import { AuthErrorMessage } from '../AuthErrorMessage';
import { isRateLimitError, isTurnstileError } from '../authErrors';
import { AuthField } from '../AuthField';
import css from './LoginForm.module.scss';

const EMAIL_ALREADY_REGISTERED_MESSAGE =
  'This email is already registered. Sign in below to continue.';

const TURNSTILE_CONTAINER_ID = 'turnstile-login';
const PRIVACY_URL = 'https://starzero.ai/privacy';

export type LoginMode = 'magic-link' | 'password';

function getSubmitLabel(mode: LoginMode, loading: boolean): string {
  if (mode === 'password') return loading ? 'Signing in' : 'Sign in';
  return loading ? 'Sending link...' : 'Continue with email';
}

// Carry the email the user already typed (plus any post-login redirect) across
// a method switch so changing methods never loses their input — the form reads
// `?email=` on mount, so a query param is the simplest bridge.
function withAuthParams(path: string, email: string, redirect: string | null): string {
  const search = new URLSearchParams();
  if (email) search.set('email', email);
  if (redirect) search.set('redirect', redirect);
  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
}

// The login method toggle points at the preference-setter routes
// (/login/with-password, /login/magic-link): visiting one records the device's
// preferred method and bounces to /login, which then renders that method.
function toggleHref(toMode: LoginMode, email: string, redirect: string | null): string {
  return withAuthParams(
    toMode === 'password' ? '/login/with-password' : '/login/magic-link',
    email,
    redirect,
  );
}

export const LoginForm = observer(({ forceMagicLink = false }: { forceMagicLink?: boolean }) => {
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get('redirect');
  // The login screen stays at /login and shows whichever method this device last
  // used (default magic-link when nothing is remembered). `forceMagicLink` is for
  // /register, which must always offer the sign-up-capable magic-link form
  // regardless of a saved password preference (and without mutating it).
  const mode: LoginMode = forceMagicLink
    ? 'magic-link'
    : (loginPreferences.preferredMethod ?? 'magic-link');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputError, setInputError] = useState<string | null>(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const cooldown = useResendCooldown();

  const { getToken, reset: resetTurnstile } = useTurnstile({
    containerId: TURNSTILE_CONTAINER_ID,
    autoRender: mode === 'magic-link' && !magicLinkSent,
  });

  useEffect(() => {
    const emailParam = searchParams.get('email');
    const errorParam = searchParams.get('err');
    const msgParam = searchParams.get('msg');

    if (emailParam) {
      setEmail(emailParam);
    }

    if (errorParam === 'exists') {
      setError(EMAIL_ALREADY_REGISTERED_MESSAGE);
    }

    if (msgParam === 'expired') {
      setError('Your sign-in link has expired. Request a new one below.');
    }
  }, [searchParams]);

  const submitPassword = () => {
    if (!email || !password) {
      setInputError('Email and password are required');
      setError('Email and password are required');
      return;
    }
    if (!isEmailValid(email)) {
      setInputError('Invalid email');
      setError('Invalid email');
      return;
    }

    setLoading(true);
    setInputError(null);
    setError(null);
    userStore
      .login(email, password)
      .then(() => {
        // Remember (device-level) that this browser signed in with a password.
        loginPreferences.setPreferredMethod('password');
      })
      .catch((e) => {
        trackingStore.loginFailed(e.message);
        setError(e.message);
        setInputError(e.message);
      })
      .finally(() => setLoading(false));
  };

  const submitMagicLink = async () => {
    if (!email) {
      setInputError('Email is required');
      setError('Email is required');
      return;
    }
    if (!isEmailValid(email)) {
      setInputError('Invalid email');
      setError('Invalid email');
      return;
    }

    setLoading(true);
    setInputError(null);
    setError(null);
    try {
      const turnstileToken = await getToken();
      const redirect = redirectParam || undefined;
      await userStore.requestMagicLink({ email, turnstileToken, redirect });
      // Remember (device-level) that this browser prefers magic-link sign-in.
      loginPreferences.setPreferredMethod('magic-link');
      setMagicLinkSent(true);
      cooldown.start();
    } catch (e) {
      // The single-use Turnstile token was consumed by the request above. Reset
      // on every failure so a retry fetches a fresh token — otherwise the next
      // attempt reuses the spent token and fails the (now genuine) Turnstile
      // check. See useTurnstile.
      resetTurnstile();
      trackingStore.magicLinkRequestFailed(e instanceof Error ? e.message : 'unknown');
      if (isTurnstileError(e)) {
        setError('Verification failed. Please complete the check again.');
      } else if (isRateLimitError(e)) {
        setError('Too many attempts. Please try again later.');
      } else if (e instanceof ApiError) {
        setError(e.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    if (cooldown.active) return;
    resetTurnstile();
    setMagicLinkSent(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    if (mode === 'password') {
      submitPassword();
    } else {
      submitMagicLink();
    }
  };

  if (magicLinkSent) {
    return (
      <Box className={css.loginFormBox}>
        <Box className={css.loginHeaderText}>
          <h1>Check your inbox</h1>
          <span>
            If that email is valid, we've sent a sign-in link. Open it to finish signing in.
          </span>
        </Box>
        <div>
          <AuthButton type="button" disabled={cooldown.active} onClick={handleResend}>
            {cooldown.active ? `Resend in ${cooldown.secondsLeft}s` : 'Resend link'}
          </AuthButton>
          <div className={css.backToLogin}>
            <span className={css.backLink} onClick={() => setMagicLinkSent(false)}>
              Use a different email
            </span>
          </div>
        </div>
      </Box>
    );
  }

  const isMagicLink = mode === 'magic-link';

  return (
    <Box className={css.loginFormBox}>
      <Box className={css.loginHeaderText}>
        <h1>{isMagicLink ? 'Log in or sign up' : 'Log in'}</h1>
        <span>
          {isMagicLink
            ? "Enter your email and we'll send you a link to sign in, or create your account if you're new."
            : 'Welcome back! Please enter your details.'}
        </span>
      </Box>
      <form className={css.form} onSubmit={handleSubmit}>
        <div className={css.loginFormFields}>
          <AuthField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            // Always focus the email field on mount. Switching methods remounts
            // this form (via the preference-setter redirect), so this re-focuses
            // email after every switch while the typed address is carried over
            // in the ?email= param.
            autoFocus
            required
            error={!!inputError}
          />
          {!isMagicLink && (
            <AuthField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!inputError}
              autoComplete="current-password"
              required
            />
          )}
        </div>
        {error && (
          <AuthErrorMessage
            message={error}
            type={error === EMAIL_ALREADY_REGISTERED_MESSAGE ? 'warning' : 'error'}
            className={css.errorMessageMarginTop}
          />
        )}
        {!isMagicLink && (
          <div className={css.forgotPassword}>
            <span>&nbsp;</span>
            <Link to="/reset-password" onClick={() => trackingStore.forgotPasswordClicked()}>
              Forgot password
            </Link>
          </div>
        )}
        <div>
          {/* Inside the button wrapper (not the gapped form flow) so the
              invisible widget adds no layout gap. */}
          {isMagicLink && <div id={TURNSTILE_CONTAINER_ID} className={css.turnstileContainer} />}
          <AuthButton type="submit" disabled={loading} shineBehind>
            {getSubmitLabel(mode, loading)}
          </AuthButton>
          {isMagicLink && (
            <p className={css.legalText}>
              By continuing, you acknowledge It Just Works's{' '}
              <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
              .
            </p>
          )}
          <div className={css.secondaryToggle}>
            {!isMagicLink ? (
              <Link className={css.toggleLink} to={toggleHref('magic-link', email, redirectParam)}>
                Email me a sign-in link instead
              </Link>
            ) : forceMagicLink ? (
              // Register view (/register): switch to creating an account with a
              // password instead of the magic-link sign-up.
              <Link
                className={css.toggleLink}
                to={withAuthParams('/register/with-password', email, redirectParam)}
              >
                Sign up with a password
              </Link>
            ) : (
              <Link className={css.toggleLink} to={toggleHref('password', email, redirectParam)}>
                Sign in with password
              </Link>
            )}
          </div>
        </div>
      </form>
    </Box>
  );
});
